import { useState, useEffect, useMemo } from 'react';
import { X, Pencil, RotateCcw } from 'lucide-react';
import { api } from '../api/client';
import type { TranscriptContent, TranscriptSpeakers } from '../types';
import { useT } from '../i18n';
import { RedoTranscriptForm } from './RedoTranscript';

interface Props {
  transcriptionId: number;
  title: string;
  onClose: () => void;
}

interface Segment {
  speaker: string;
  /** Speakr's original diarization label (SPEAKER_00…), kept when the speaker is named. */
  speaker_label?: string;
  sentence: string;
  start_time: number;
  end_time: number;
}

interface SpeakerBlock {
  speaker: string;
  label: string;
  startTime: number;
  endTime: number;
  lines: string[];
}

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function groupSegments(segments: Segment[]): SpeakerBlock[] {
  const blocks: SpeakerBlock[] = [];
  for (const seg of segments) {
    const last = blocks[blocks.length - 1];
    const label = seg.speaker_label || seg.speaker;
    if (last && last.speaker === seg.speaker && last.label === label) {
      last.endTime = seg.end_time;
      last.lines.push(seg.sentence);
    } else {
      blocks.push({
        speaker: seg.speaker,
        label,
        startTime: seg.start_time,
        endTime: seg.end_time,
        lines: [seg.sentence],
      });
    }
  }
  return blocks;
}

function parseTranscript(raw: string): SpeakerBlock[] | null {
  try {
    const segments: Segment[] = JSON.parse(raw);
    if (Array.isArray(segments) && segments.length > 0 && segments[0].speaker !== undefined) {
      return groupSegments(segments);
    }
  } catch { /* not JSON — fall through */ }
  return null;
}

// Assign a stable color index per speaker
const DEFAULT_LABEL = /^SPEAKER_(\d+)$/i;

/** One voice's name editor: closest voices first, then any name. */
function SpeakerEditor({ info, people, onSave, onCancel, busy }: {
  info: TranscriptSpeakers['speakers'][number] | undefined;
  people: string[];
  onSave: (name: string) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const { t } = useT();
  const [name, setName] = useState(info?.name || '');
  const listId = `speaker-people-${info?.label || 'x'}`;
  return (
    <div className="speaker-editor">
      {info && info.suggestions.length > 0 && (
        <div className="speaker-editor-row">
          <span className="speaker-editor-caption">{t('transcript.speakerClosest')}</span>
          <div className="filter-chips">
            {info.suggestions.map(s => (
              <button key={s.key} type="button" className={`chip ${name === s.name ? 'active' : ''}`}
                disabled={busy} onClick={() => setName(s.name)}>
                {s.name} · {Math.round(s.similarity * 100)} %
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="speaker-editor-row">
        <input className="form-input" list={listId} value={name} autoFocus disabled={busy}
          placeholder={t('transcript.speakerPlaceholder')}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && name.trim()) onSave(name.trim()); if (e.key === 'Escape') onCancel(); }} />
        <datalist id={listId}>{people.map(p => <option key={p} value={p} />)}</datalist>
        <button type="button" className="btn btn-accent btn-sm" disabled={busy || !name.trim()} onClick={() => onSave(name.trim())}>
          {t('common.save')}
        </button>
        {info?.name && (
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onSave('')}>
            {t('transcript.speakerClear')}
          </button>
        )}
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={onCancel}>{t('common.cancel')}</button>
      </div>
      <div className="speaker-editor-hint">{t('transcript.speakerHint')}</div>
    </div>
  );
}

const SPEAKER_COLORS = [
  'var(--accent)',
  '#e67e22',
  '#27ae60',
  '#8e44ad',
  '#2980b9',
  '#c0392b',
  '#16a085',
  '#d35400',
];

export function TranscriptModal({ transcriptionId, title, onClose }: Props) {
  const { t } = useT();
  const [data, setData] = useState<TranscriptContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'transcript' | 'summary'>('transcript');
  const [speakers, setSpeakers] = useState<TranscriptSpeakers | null>(null);
  const [editing, setEditing] = useState<{ label: string; block: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [redoing, setRedoing] = useState(false);

  useEffect(() => {
    setLoading(true);
    (api.getTranscriptContent(transcriptionId) as Promise<TranscriptContent>)
      .then(d => {
        setData(d);
        setTab(d.transcript ? 'transcript' : 'summary');
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    (api.getTranscriptSpeakers(transcriptionId) as Promise<TranscriptSpeakers>)
      .then(setSpeakers)
      .catch(() => setSpeakers(null));
  }, [transcriptionId]);

  const speakerInfo = useMemo(
    () => new Map((speakers?.speakers || []).map(s => [s.label, s])),
    [speakers],
  );

  const shownName = (block: SpeakerBlock) => {
    const m = DEFAULT_LABEL.exec(block.speaker);
    return m ? t('transcript.voice', { n: Number(m[1]) + 1 }) : block.speaker;
  };

  const saveName = async (label: string, name: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      const r = await api.renameTranscriptSpeaker(transcriptionId, label, name) as TranscriptContent & TranscriptSpeakers;
      setData(d => d ? { ...d, transcript: r.transcript, summary: r.summary ?? d.summary } : d);
      setSpeakers({ enabled: r.enabled, speakers: r.speakers, shares: r.shares, people: r.people });
      setEditing(null);
    } catch (err) {
      setSaveError(t('transcript.speakerFailed', { error: (err as Error).message }));
    } finally {
      setSaving(false);
    }
  };

  const blocks = useMemo(() => {
    if (!data?.transcript) return null;
    return parseTranscript(data.transcript);
  }, [data?.transcript]);

  const speakerColorMap = useMemo(() => {
    if (!blocks) return new Map<string, string>();
    const map = new Map<string, string>();
    let idx = 0;
    for (const b of blocks) {
      if (!map.has(b.speaker)) {
        map.set(b.speaker, SPEAKER_COLORS[idx % SPEAKER_COLORS.length]);
        idx++;
      }
    }
    return map;
  }, [blocks]);

  return (
    <div className="modal-overlay" style={{ zIndex: 300 }} onClick={onClose}>
      <div className="modal modal-full" style={{ maxWidth: 760 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="close-btn" onClick={onClose}><X size={12} /></button>
        </div>
        <div className="modal-body">
          {loading && <div className="spinner" style={{ margin: '24px auto' }} />}
          {!loading && !data && (
            <div style={{ color: 'var(--ink-3)' }}>{t('transcript.loadFailed')}</div>
          )}
          {!loading && data && (
            <>
              {data.can_redo && (
                redoing ? (
                  <RedoTranscriptForm transcriptionId={transcriptionId} onDone={onClose} onCancel={() => setRedoing(false)} />
                ) : (
                  <div className="transcript-toolbar">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRedoing(true)}>
                      <RotateCcw size={12} /> {t('transcript.redo')}
                    </button>
                  </div>
                )
              )}
              {data.transcript && data.summary && (
                <div className="tabs" style={{ marginBottom: 'var(--s16)' }}>
                  <button
                    className={`tab ${tab === 'transcript' ? 'active' : ''}`}
                    onClick={() => setTab('transcript')}
                  >{t('transcript.tabTranscript')}</button>
                  <button
                    className={`tab ${tab === 'summary' ? 'active' : ''}`}
                    onClick={() => setTab('summary')}
                  >{t('transcript.tabSummary')}</button>
                </div>
              )}

              {tab === 'transcript' && speakers && speakers.shares.length > 0 && (
                <div className="transcript-shared">
                  {t('transcript.sharedWith', { names: speakers.shares.map(s => s.name).join(', ') })}
                  {speakers.shares.some(s => s.simulated) && <> {t('transcript.sharedSimulated')}</>}
                </div>
              )}
              {tab === 'transcript' && saveError && <div className="transcript-error">{saveError}</div>}

              {tab === 'transcript' && data.transcript && (
                blocks ? (
                  <div className="transcript-blocks">
                    {blocks.map((block, i) => {
                      const info = speakerInfo.get(block.label);
                      return (
                      <div key={i} className="transcript-block">
                        <div className="transcript-block-header">
                          {speakers?.enabled ? (
                            <button
                              type="button"
                              className="transcript-speaker transcript-speaker-edit"
                              style={{ color: speakerColorMap.get(block.speaker) }}
                              title={t('transcript.speakerEdit')}
                              onClick={() => { setSaveError(null); setEditing(editing?.block === i ? null : { label: block.label, block: i }); }}
                            >
                              {shownName(block)} <Pencil size={11} />
                            </button>
                          ) : (
                            <span
                              className="transcript-speaker"
                              style={{ color: speakerColorMap.get(block.speaker) }}
                            >
                              {shownName(block)}
                            </span>
                          )}
                          <span className="transcript-time">
                            {formatTime(block.startTime)} – {formatTime(block.endTime)}
                          </span>
                          {info?.source === 'voice' && info.similarity !== null && (
                            <span className="transcript-time">· {t('transcript.speakerAuto', { pct: Math.round(info.similarity * 100) })}</span>
                          )}
                        </div>
                        {editing?.block === i && (
                          <SpeakerEditor
                            key={`${block.label}-${block.speaker}`}
                            info={info}
                            people={speakers?.people || []}
                            busy={saving}
                            onCancel={() => setEditing(null)}
                            onSave={name => saveName(block.label, name)}
                          />
                        )}
                        <div className="transcript-block-text">
                          {block.lines.join(' ')}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="transcript-text">{data.transcript}</div>
                )
              )}

              {tab === 'summary' && data.summary && (
                <div className="transcript-summary">{data.summary}</div>
              )}

              {!data.transcript && !data.summary && (
                <div style={{ color: 'var(--ink-3)' }}>{t('transcript.noContent')}</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
